import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BusinessConsultantChat } from '../../../types/businessConsultant';

interface BusinessConsultantCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tabId: string) => void;
  chats: BusinessConsultantChat[];
}

interface Command {
  id: string;
  title: string;
  description: string;
  icon: string;
  action: () => void;
  category: 'navigation' | 'action' | 'chat';
}

const BusinessConsultantCommandPalette: React.FC<BusinessConsultantCommandPaletteProps> = ({
  isOpen,
  onClose,
  onNavigate,
  chats
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build commands list
  const commands: Command[] = [
    // Navigation
    {
      id: 'nav-chat',
      title: 'Go to Chat',
      description: 'Start or continue conversations',
      icon: '💬',
      action: () => onNavigate('chat'),
      category: 'navigation'
    },
    {
      id: 'nav-knowledge',
      title: 'Go to Knowledge',
      description: 'Manage consultant knowledge base',
      icon: '📚',
      action: () => onNavigate('knowledge'),
      category: 'navigation'
    },
    {
      id: 'nav-history',
      title: 'Go to History',
      description: 'Browse conversation history',
      icon: '🕐',
      action: () => onNavigate('history'),
      category: 'navigation'
    },
    {
      id: 'nav-settings',
      title: 'Go to Settings',
      description: 'Configure consultant preferences',
      icon: '⚙️',
      action: () => onNavigate('settings'),
      category: 'navigation'
    },
    // Actions
    {
      id: 'action-new-chat',
      title: 'Start New Chat',
      description: 'Begin a new conversation',
      icon: '✨',
      action: () => {
        onNavigate('chat');
        onClose();
      },
      category: 'action'
    },
    {
      id: 'action-crm',
      title: 'Open CRM',
      description: 'View CRM data for context',
      icon: '👥',
      action: () => {
        navigate('/crm');
        onClose();
      },
      category: 'action'
    },
    {
      id: 'action-plans',
      title: 'Open Business Plans',
      description: 'View business plans for context',
      icon: '📄',
      action: () => {
        navigate('/business-plan');
        onClose();
      },
      category: 'action'
    },
    // Recent Chats
    ...chats.slice(0, 5).map(chat => ({
      id: `chat-${chat.id}`,
      title: chat.chat_name || 'Untitled Chat',
      description: `Last updated ${new Date(chat.updated_at || '').toLocaleDateString()}`,
      icon: '💬',
      action: () => {
        onNavigate('chat');
        // TODO: Select this chat in ChatTab
        onClose();
      },
      category: 'chat' as const
    }))
  ];

  // Filter commands based on search
  const filteredCommands = search
    ? commands.filter(cmd =>
        cmd.title.toLowerCase().includes(search.toLowerCase()) ||
        cmd.description.toLowerCase().includes(search.toLowerCase())
      )
    : commands;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => (i + 1) % filteredCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => (i - 1 + filteredCommands.length) % filteredCommands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        filteredCommands[selectedIndex]?.action();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, onClose]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Command Palette */}
      <div className="flex min-h-screen items-start justify-center p-4 pt-[10vh]">
        <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl ring-1 ring-black/5">
          {/* Search Input */}
          <div className="border-b border-gray-200 px-4">
            <div className="flex items-center gap-3 py-3">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search actions, chats, or navigate..."
                className="flex-1 border-0 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
              <kbd className="hidden sm:inline-flex items-center rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-500">
                ESC
              </kbd>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {filteredCommands.length === 0 ? (
              <div className="py-12 text-center">
                <div className="text-4xl mb-2">🔍</div>
                <p className="text-sm text-gray-500">No results found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredCommands.map((command, index) => (
                  <button
                    key={command.id}
                    onClick={() => {
                      command.action();
                      onClose();
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`
                      w-full rounded-lg px-4 py-3 text-left transition
                      ${index === selectedIndex
                        ? 'bg-blue-50 ring-2 ring-blue-500'
                        : 'hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{command.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{command.title}</div>
                        <div className="text-sm text-gray-500 truncate">{command.description}</div>
                      </div>
                      {command.category === 'navigation' && (
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">↑</kbd>
                  <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">↓</kbd>
                  <span>Navigate</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">↵</kbd>
                  <span>Select</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">ESC</kbd>
                <span>Close</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessConsultantCommandPalette;
